import React, { useCallback, useEffect, useState } from 'react';
import { permissionMapApi } from '../../services/api';

/**
 * The permission map (GET /api/admin/permission-map): what every key opens, read by the server from
 * what is enforced — [Require*] attributes, navigation rows, table owners, the keys the write check
 * builds and the inline checks it lists. Fetched each time a page that shows it opens, so a change
 * to a row, an owner or a role default shows the next time the page is opened.
 */
export const usePermissionMap = () => {
  const [map, setMap] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    setError(null);
    return permissionMapApi.map()
      .then(res => setMap(res.data))
      .catch(err => setError(err.response?.data?.error || 'Could not load the permission map'));
  }, []);
  useEffect(() => { load(); }, [load]);
  return { map, error, reload: load };
};

/**
 * A titled section holding a standard grid (.stui-related-section around .stui-data-grid).
 * `columns` is [{ label, width }]; `rows` is [{ key, cells, onClick, onDoubleClick, selected, inactive }].
 * A cell is a value, or { value, className, title } to style its td. `height` fixes the section's
 * height so a long grid scrolls under its own sticky header; `actions` sit at the header's right.
 */
const isCellSpec = c => c !== null && typeof c === 'object' && !React.isValidElement(c) && 'value' in c;

export const GridSection = ({ title, columns, rows, empty = 'Nothing to show', height, className = '', actions }) => (
  <div className={`stui-related-section stui-mt-md${className ? ` ${className}` : ''}`} style={height ? { height } : undefined}>
    <div className="stui-detail-section-header stui-related-header">
      <span>{title}</span>
      {actions && <div className="stui-row-8">{actions}</div>}
    </div>
    <div className="stui-related-content">
      <div className="stui-data-grid-container">
        <div className="stui-data-grid-scroll-area">
          <table className="stui-data-grid">
            <thead><tr>{columns.map(c => <th key={c.label} style={c.width ? { width: c.width } : undefined}>{c.label}</th>)}</tr></thead>
            <tbody>
              {rows.length === 0
                ? <tr><td colSpan={columns.length} className="stui-td-empty">{empty}</td></tr>
                : rows.map(r => (
                  <tr key={r.key} onClick={r.onClick} onDoubleClick={r.onDoubleClick}
                    className={[(r.onClick || r.onDoubleClick) && 'stui-clickable', r.selected && 'stui-row-selected', r.inactive && 'stui-inactive'].filter(Boolean).join(' ') || undefined}>
                    {r.cells.map((c, i) => (isCellSpec(c)
                      ? <td key={i} className={c.className} title={c.title}>{c.value}</td>
                      : <td key={i}>{c}</td>))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
);

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * Everything one key does, as standard grids, with its problems as hint lines above them. `name`
 * puts the key in each grid's title (the grant screen, where the grids sit under other ones).
 */
export const KeyUses = ({ entry, name }) => {
  const t = words => (name ? `${name}: ${words}` : words.charAt(0).toUpperCase() + words.slice(1));
  if (!entry) return <p className="stui-hint">Nothing in the app checks this key.</p>;
  const none = !entry.navRows.length && !entry.endpoints.length && !entry.tables.length && !entry.verbs.length && !entry.inline.length;
  return (
    <>
      {entry.flags.map(f => <p key={f} className="stui-hint">Problem: {f}</p>)}
      {none && <p className="stui-hint">Nothing in the app checks this key.</p>}
      {entry.navRows.length > 0 && (
        <GridSection title={`${t('pages and sections')} (${entry.navRows.length})`}
          columns={[{ label: 'Page or section', width: '40%' }, { label: 'What the key does there' }]}
          rows={entry.navRows.map(n => ({ key: n.navKey, cells: [n.path, n.how] }))} />
      )}
      {entry.tables.length > 0 && (
        <GridSection title={`${t('tables it owns')} (${entry.tables.length})`}
          columns={[{ label: 'Table', width: '30%' }, { label: 'Category', width: '20%' }, { label: 'What owning means' }]}
          rows={entry.tables.map(t => ({ key: t.table, cells: [t.table, t.category, 'Needed to read the table, and to save to it from any page.'] }))} />
      )}
      {entry.verbs.length > 0 && (
        <GridSection title={t('asked for by the write check')}
          columns={[{ label: 'What it does' }]}
          rows={entry.verbs.map(v => ({ key: v, cells: [v] }))} />
      )}
      {entry.inline.length > 0 && (
        <GridSection title={`${t('checked in code')} (${entry.inline.length})`}
          columns={[{ label: 'File', width: '25%' }, { label: 'What it does' }, { label: 'Enforced by', width: '15%' }]}
          rows={entry.inline.map(i => ({ key: i.file + i.what, cells: [i.file.split('/').pop(), i.what, i.serverEnforced ? 'The API' : 'The browser only'] }))} />
      )}
      {entry.endpoints.length > 0 && (
        <p className="stui-hint stui-mt-md">Also required by {plural(entry.endpoints.length, 'API endpoint')}.</p>
      )}
    </>
  );
};
